import { ApiError } from '@graal/api';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { TriangleAlertIcon } from 'lucide-react';
import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ProblemReference } from '@/components/patterns/problem-reference';
import { Button } from '@/components/ui/button';
import { reportClientError } from '@/lib/client-errors';

/**
 * Ce que voit l'utilisateur quand un écran plante (rendu ou chargement de route) : un message
 * traduit, et — si l'échec vient de l'API et qu'elle a posé une référence (`traceId` du corps
 * `Error`, sinon `X-Request-Id`) — « Référence : xxxxxxxx » à transmettre au support. Pas de
 * télémétrie externe : rien ne part ailleurs que vers graal-api.
 *
 * Aucun hook du routeur ici : la même vue sert la frontière racine (`AppErrorBoundary`), qui
 * entoure `RouterProvider` et peut donc s'afficher hors de tout contexte de routeur.
 */
export function ErrorFallback({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const reference = error instanceof ApiError ? error.traceId : undefined;
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 py-14 text-center"
      role="alert"
      data-testid="error-boundary"
    >
      <span className="inline-flex size-9 items-center justify-center rounded-xl bg-danger-subtle text-danger-text">
        <TriangleAlertIcon className="size-4.5" aria-hidden />
      </span>
      <div className="max-w-md space-y-1">
        <p className="text-body font-medium text-foreground">{t('errorBoundary.title')}</p>
        <p className="text-control text-muted-foreground">{t('errorBoundary.description')}</p>
        {reference && <ProblemReference traceId={reference} className="pt-1" />}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <Button type="button" size="sm" onClick={onRetry}>
            {t('errorBoundary.retry')}
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => window.location.reload()}>
          {t('errorBoundary.reload')}
        </Button>
        <Button variant="ghost" size="sm" asChild>
          <a href="/">{t('errorBoundary.home')}</a>
        </Button>
      </div>
    </div>
  );
}

/**
 * `defaultErrorComponent` du routeur : toute route dont le rendu, le `beforeLoad` ou le `loader`
 * lève une erreur l'affiche à la place de l'écran, la coquille autour restant en place.
 * « Réessayer » relance le chargement de la route puis réinitialise la frontière.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  // Une fois par erreur affichée ; le rapporteur dédoublonne (StrictMode rejoue les effets).
  useEffect(() => reportClientError('render', error), [error]);
  return (
    <ErrorFallback
      error={error}
      onRetry={() => {
        reset();
        void router.invalidate();
      }}
    />
  );
}

/**
 * Frontière racine, AUTOUR de `RouterProvider` : attrape ce qui échappe aux frontières de route
 * (le routeur lui-même, les fournisseurs). Seule issue : recharger la page.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown, info: ErrorInfo) => void },
  { error: unknown; failed: boolean }
> {
  override state = { error: undefined as unknown, failed: false };

  static getDerivedStateFromError(error: unknown) {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    reportClientError('render', error);
    this.props.onError?.(error, info);
  }

  override render() {
    return this.state.failed ? <ErrorFallback error={this.state.error} /> : this.props.children;
  }
}
